import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Loader2, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from 'react-hot-toast';

type CareerApplicationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  jobTitle?: string;
};

export function CareerApplicationDrawer({ isOpen, onClose, jobTitle }: CareerApplicationDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resume, setResume] = useState<File | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0]);
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!resume) {
      toast.error("Please upload your resume");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      formData.append('resume', resume);
      if (jobTitle) formData.append('position', jobTitle);
      
      const response = await fetch("https://getform.io/f/azynzweb", {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json"
        }
      });
      
      if (!response.ok) {
        throw new Error("Submission failed");
      }
      
      setShowConfirmation(true);
      setIsSubmitting(false);
      setTimeout(() => {
        onClose();
        setShowConfirmation(false);
      }, 3000);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to submit application");
      setIsSubmitting(false);
    }
  };

  if (showConfirmation) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="fixed inset-0 z-50 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-purple-50/50" onClick={onClose} />
            
            <motion.div 
              className="absolute right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-white to-purple-50 shadow-xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30 }}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b">
                  <h2 className="text-2xl font-bold text-purple-800">
                    Application Confirmation
                  </h2>
                  <button onClick={onClose} className="text-purple-700 hover:text-purple-900">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="mx-auto w-full max-w-2xl p-8 text-center">
                  <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold text-purple-800 mb-2">Application Submitted!</h3>
                  <p className="text-purple-600 mb-6">
                    Thank you for applying to {jobTitle}. We'll review your application and contact you soon.
                  </p>
                  <Button 
                    onClick={() => {
                      onClose();
                      setShowConfirmation(false);
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-purple-50/50" onClick={onClose} />
          
          <motion.div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-white to-purple-50 shadow-xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold text-purple-800">
                  {jobTitle ? `Apply for ${jobTitle}` : 'Career Application'}
                </h2>
                <button onClick={onClose} className="text-purple-700 hover:text-purple-900">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={onSubmit} className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-purple-700">Full Name</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      required 
                      className="bg-white border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-purple-700">Email</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      required 
                      className="bg-white border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-purple-700">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      type="tel" 
                      required 
                      className="bg-white border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                    />
                  </div>
                  
                  <div className="relative">
                    <Label htmlFor="resume" className="text-purple-700">Resume/CV</Label>
                    <Input
                      id="resume"
                      name="resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex items-center px-4 py-2 border rounded-md text-white bg-purple-600">
                      <span>{resume ? resume.name : 'Choose file...'}</span>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      Accepted formats: PDF, DOC, DOCX (Max 5MB)
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-purple-700">Cover Letter (Optional)</Label>
                    <Textarea 
                      id="message" 
                      name="message" 
                      rows={5} 
                      className="bg-white border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                    />
                  </div>
                </div>
                
                <div className="mt-8 flex justify-end space-x-2">
                  <Button 
                    variant="outline" 
                    type="button" 
                    onClick={onClose} 
                    className="text-purple-700 hover:text-purple-900"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-md hover:shadow-lg transition-all"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
